import {
  CurrencyConfig,
  CurrencyOnlyQuestion,
  FormQuestion,
  MoneyQuestion,
  NominationForm,
  NominationFormPage,
} from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';
import {
  OPERATING_BUDGET_AMOUNT_KEY,
  OPERATING_BUDGET_CURRENCY_KEY,
  getReservedKeySegment,
} from '@percent/grants-nomination-form/constants/operatingBudget';

/**
 * The raw amount/currency questions arrive as plain `number`/`text` questions that also carry the authored
 * currency config. `FormQuestion` describes the rendered shapes rather than the wire shape, so this is the one
 * place that reads the extra fields off the payload before they are lifted onto the fused question.
 */
const readCurrencyConfig = (question?: FormQuestion): CurrencyConfig => {
  const raw = question as (FormQuestion & CurrencyConfig) | undefined;

  return { defaultCurrency: raw?.defaultCurrency, allowedCurrencies: raw?.allowedCurrencies };
};

// Currency config is authorable on the currency half only, so that half governs whenever it exists. The amount
// half is still read as a fallback for imported programs, which skip the cross-field validation and can carry
// stale config; an amount-only question therefore falls through to DEFAULT_OPERATING_BUDGET_CURRENCY.
const currencyConfigOf = (amount?: FormQuestion, currency?: FormQuestion): CurrencyConfig =>
  readCurrencyConfig(currency ?? amount);

const buildMoneyQuestion = (amount: FormQuestion, currency?: FormQuestion): MoneyQuestion => ({
  id: amount.id,
  key: amount.key,
  label: amount.label,
  description: amount.description,
  instructions: amount.instructions,
  placeholder: amount.placeholder,
  required: amount.required,
  order: amount.order,
  hidden: amount.hidden,
  ...currencyConfigOf(amount, currency),
  type: 'money',
  amountKey: amount.key,
  currencyKey: currency?.key,
  amountQuestionId: amount.id,
  currencyQuestionId: currency?.id,
  amountRequired: amount.required,
});

const toCurrencyOnlyQuestion = (q: FormQuestion): CurrencyOnlyQuestion => ({
  id: q.id,
  key: q.key,
  label: q.label,
  description: q.description,
  instructions: q.instructions,
  placeholder: q.placeholder,
  required: q.required,
  order: q.order,
  hidden: q.hidden,
  ...currencyConfigOf(undefined, q),
  type: 'currency',
});

type MoneyHalf = { group: string; half: 'amount' | 'currency' };
type MoneyGroup = { amount?: FormQuestion; currency?: FormQuestion };

const MONEY_SUFFIX = /\.(amount|currency)$/;

const moneyHalfOf = (q: FormQuestion): MoneyHalf | undefined => {
  const segment = getReservedKeySegment(q.key);
  const parent = q.key.slice(0, q.key.lastIndexOf('.'));
  if (segment === OPERATING_BUDGET_AMOUNT_KEY) return { group: parent, half: 'amount' };
  if (segment === OPERATING_BUDGET_CURRENCY_KEY) return { group: parent, half: 'currency' };

  const match = q.type === 'money' ? MONEY_SUFFIX.exec(q.key) : null;
  return match ? { group: q.key.slice(0, match.index), half: match[1] as MoneyHalf['half'] } : undefined;
};

const fusePage = (page: NominationFormPage): NominationFormPage => {
  const questions = page.questions;
  if (!questions || questions.length === 0) {
    return page;
  }

  const groups = new Map<string, MoneyGroup>();
  questions.forEach((q) => {
    const money = moneyHalfOf(q);
    if (money) groups.set(money.group, { ...groups.get(money.group), [money.half]: q });
  });

  const nextQuestions = questions.flatMap((q) => {
    const money = moneyHalfOf(q);
    if (!money) return [q];

    const { amount, currency } = groups.get(money.group) ?? {};
    if (money.half === 'amount') return [buildMoneyQuestion(q, currency)];
    return amount ? [] : [toCurrencyOnlyQuestion(q)];
  });

  return { ...page, questions: nextQuestions };
};

export const fuseOperatingBudgetQuestions = (form: NominationForm): NominationForm => ({
  ...form,
  pages: form.pages.map(fusePage),
});
